/* Loomish Desk — the way to the table.
 *
 * The site is the showroom. WhatsApp is the table. This routes the enquiry
 * BEFORE it reaches WhatsApp, so every conversation starts already sorted:
 * the house knows whether it is quoting a rug or a container before it reads
 * a word.
 *
 * THE ONE RULE THAT DECIDES WHETHER THIS WORKS AT ALL
 * ---------------------------------------------------
 * window.open must be called synchronously inside the click handler. Move it
 * behind a timeout, a promise or an animation callback and every popup blocker
 * on earth eats it, silently, and the visitor thinks the site is broken. So the
 * handover happens first and the theatre plays afterwards — never the reverse.
 *
 * Nothing here talks to a server. The remembered door lives in this browser and
 * goes nowhere.
 */
(function () {
  'use strict';

  var KEY_DOOR = 'lm-desk-door';
  var KEY_ASIDE = 'lm-aside';
  var MAX_ASIDE = 20;          /* a wa.me URL is not infinite */

  var live = [];

  function store(k, v) { try { localStorage.setItem(k, v); } catch (e) {} }
  function read(k) { try { return localStorage.getItem(k); } catch (e) { return null; } }

  /* ------------------------------------------------------------- the desk */

  function Desk(root) {
    var self = this;
    var d = root.dataset;

    this.root = root;
    root.__lmd = this;
    live.push(this);

    this.panel = root.querySelector('.lmd__panel');
    this.scrim = root.querySelector('.lmd__scrim');
    this.rail = root.querySelector('[data-lmd-open]');
    this.leaving = root.querySelector('[data-lmd-leaving]');
    this.doors = Array.prototype.slice.call(root.querySelectorAll('[data-lmd-door]'));

    this.wa = d.wa;
    this.waTrade = d.waTrade || d.wa;
    this.remember = d.remember === '1';
    this.editor = d.editor === '1';
    this.open = false;

    this.pickKnowledge();
    this.markChosen();

    /* Opening */
    if (this.rail) {
      this.rail.addEventListener('click', function (e) {
        /*
         * A remembered door goes straight through — but the click that does it
         * must still be THIS click, or the blocker takes it. So the shortcut
         * runs here, inside the handler, not after a state change.
         */
        var chosen = self.remember ? self.doorById(read(KEY_DOOR)) : null;
        if (chosen && !e.shiftKey) { self.send(chosen, true); return; }
        self.show();
      });
    }

    /* Closing */
    root.querySelectorAll('[data-lmd-close]').forEach(function (el) {
      el.addEventListener('click', function () { self.hide(); });
    });

    this.onKey = function (e) {
      if (!self.open) return;
      if (e.key === 'Escape') { e.preventDefault(); self.hide(); return; }
      if (e.key === 'Tab') self.trap(e);
    };
    document.addEventListener('keydown', this.onKey);

    /* The doors */
    this.doors.forEach(function (btn) {
      btn.addEventListener('click', function () { self.send(btn, false); });
    });

    /* The fan, if this section is showing one. */
    this.fan();
    /* And the conversation it now opens onto. */
    this.lmcInit();
  }

  /* ======================================================================
     THE FAN
     ----------------------------------------------------------------------
     Four doors in front of the panel. The doors already carry data-lmd-door,
     so the constructor has ALREADY bound send() to them - which means the
     window.open still happens synchronously inside the original click, which
     is the one rule that cannot be broken here. Nothing below sends anything.
     It only opens, closes, and decides what the first door is called.
     ====================================================================== */

  Desk.prototype.fan = function () {
    var self = this;
    var fan = this.root.querySelector('[data-lmd-fan]');
    if (!fan) return;

    this.fanEl = fan;
    this.fanOpen = false;
    this.fanToggleEl = fan.querySelector('[data-lmd-fan-toggle]');
    if (!this.fanToggleEl) return;

    /* The veil belongs to the root, not the fan - see the note in the CSS. */
    this.veil = document.createElement('div');
    this.veil.className = 'lmd__fan-veil';
    this.root.appendChild(this.veil);

    this.fanContext();
    this.fanIndex();
    this.watchClearance();

    this.fanToggleEl.addEventListener('click', function () {
      self.fanOpen ? self.fanHide() : self.fanShow();
    });
    this.veil.addEventListener('click', function () { self.fanHide(); });

    /* A door was used. send() has already run on the same click; this only
       closes the fan behind it so the visitor does not come back to a menu. */
    fan.querySelectorAll('[data-lmd-door]').forEach(function (b) {
      b.addEventListener('click', function () { self.fanHide(); });
    });

    /* "Everything else" is not the rail, so the constructor never bound it. */
    fan.querySelectorAll('[data-lmd-open]').forEach(function (b) {
      b.addEventListener('click', function () { self.fanHide(); self.show(); });
    });

    /*
     * THE LEAN. Within reach, the mark drifts a little toward the cursor and
     * lets go when you leave. It is the difference between a button that was
     * drawn and an object that is present - the eye reads intent in anything
     * that responds before it is touched.
     *
     * Pointer-fine only. On a phone there is no cursor to lean toward, and a
     * finger arriving is already contact.
     */
    this.leanOn = window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    if (this.leanOn) {
      var REACH = 120;   /* px from the mark's centre where it starts to notice */
      var PULL = 0.22;   /* how far it gives - past a quarter it reads as loose */
      var frame = 0;

      this.onLean = function (e) {
        if (frame) return;                     /* one read per frame, never per event */
        frame = window.requestAnimationFrame(function () {
          frame = 0;
          var el = self.fanToggleEl;
          if (!el) return;
          var r = el.getBoundingClientRect();
          var dx = e.clientX - (r.left + r.width / 2);
          var dy = e.clientY - (r.top + r.height / 2);
          var dist = Math.sqrt(dx * dx + dy * dy);

          if (dist > REACH) {
            el.style.transform = '';
            el.classList.remove('is-near');
            return;
          }
          /* Eases off at the edge of reach, so nothing snaps on arrival. */
          var f = (1 - dist / REACH) * PULL;
          el.style.transform = 'translate3d(' + (dx * f).toFixed(2) + 'px,' + (dy * f).toFixed(2) + 'px,0)';
          el.classList.add('is-near');
        });
      };
      window.addEventListener('pointermove', this.onLean, { passive: true });
    }

    this.onFanKey = function (e) {
      if (!self.fanOpen) return;
      if (e.key === 'Escape') { e.preventDefault(); self.fanHide(); self.fanToggleEl.focus(); }
    };
    document.addEventListener('keydown', this.onFanKey);

    /* Anywhere outside closes it. pointerdown rather than click, so it closes
       on the way down and never fights the thing being tapped. */
    this.onFanOut = function (e) {
      if (!self.fanOpen) return;
      if (fan.contains(e.target)) return;
      self.fanHide();
    };
    document.addEventListener('pointerdown', this.onFanOut);
  };

  /*
   * The first door is about whatever is on screen. With nothing set aside, no
   * piece and no collection it has nothing to be about, so it is removed
   * rather than shown empty - a dead option is worse than a shorter menu.
   */
  Desk.prototype.fanContext = function () {
    var fan = this.fanEl;
    if (!fan) return;
    var btn = fan.querySelector('[data-lmd-ctx]');
    if (!btn) return;
    var label = btn.querySelector('[data-lmd-fd-label]');
    var d = this.root.dataset;

    var text = '';
    if (this.aside().length) text = btn.dataset.labelCart;
    else if (d.piece) text = btn.dataset.labelPiece;
    else if (d.room) text = btn.dataset.labelRoom;

    if (!text) { btn.hidden = true; return; }
    if (label) label.textContent = text;

    /* The label and the message have to agree. "What else is in this
       collection" cannot send "is this one still here?" — that is a door that
       says one thing and does another, and the visitor notices even if they
       could not tell you what was wrong. Each context carries its own. */
    var fill = '';
    if (this.aside().length) fill = btn.dataset.prefillCart;
    else if (d.piece) fill = btn.dataset.prefillPiece;
    else if (d.room) fill = btn.dataset.prefillRoom;
    if (fill) btn.dataset.prefill = fill;

    btn.hidden = false;
  };

  /**
   * THE MARK STEPS ASIDE.
   *
   * A floating mark sits over the page, which is the deal you accept when you
   * float something. What is not acceptable is sitting over the one button a
   * customer came to press: on a phone the mark was covering the right end of
   * Add to cart.
   *
   * So it watches for a primary action and lifts just far enough to clear it,
   * on the same spring as the doors. Give anything `data-lmd-keepclear` and
   * the mark will treat it the same way.
   *
   * Reads one rect per frame, only on scroll, only while closed. When the fan
   * is open the measurement is frozen so the doors do not walk up the screen
   * underneath the visitor.
   */
  Desk.prototype.watchClearance = function () {
    var self = this;
    if (!this.fanEl || !this.fanToggleEl) return;

    /* data-ls-add is the shop's own add button; the form selector catches any
       other cart form; data-lmd-keepclear is the hook for anything else that
       should never be sat on. */
    var sel = '[data-ls-add], form[action*="/cart/add"] [type="submit"], [data-lmd-keepclear]';
    var ticking = false;

    /* A cart drawer lives in the DOM with a real rect while it is shut. Asking
       for it to be genuinely visible keeps the mark from dodging a button
       nobody can see. */
    function onScreen(el) {
      if (el.hidden) return false;
      if (typeof el.checkVisibility === 'function') {
        return el.checkVisibility({ checkOpacity: true, checkVisibilityCSS: true });
      }
      return !!el.offsetParent;
    }

    function measure() {
      ticking = false;
      if (self.fanOpen) return;
      var fan = self.fanEl;
      var targets = document.querySelectorAll(sel);
      if (!targets.length) { fan.style.setProperty('--lmd-lift', '0px'); return; }

      /* MEASURE FROM WHERE THE MARK RESTS, NOT WHERE IT IS SITTING.
         The rect already carries the lift, so reading it raw makes the test
         flip true the moment the mark is clear — lift resets, button is
         covered again, and the mark oscillates.

         Subtracting the --lmd-lift value is not enough either: the lift is
         SPRUNG, so for 420ms the rect carries a partial translation while the
         variable already holds the destination. The two disagree on every
         frame of the animation, which is its own oscillation.

         So take the translation actually rendered this frame, straight off
         the computed matrix. It agrees with the rect by definition, animating
         or still, and the calculation becomes idempotent. */
      var rendered = 0;
      var tf = getComputedStyle(fan).transform;
      if (tf && tf !== 'none') {
        if (typeof DOMMatrixReadOnly === 'function') {
          rendered = new DOMMatrixReadOnly(tf).m42;
        } else {
          var parts = tf.match(/matrix\(([^)]+)\)/);
          if (parts) rendered = parseFloat(parts[1].split(',')[5]) || 0;
        }
      }
      var m = self.fanToggleEl.getBoundingClientRect();
      var restTop = m.top - rendered;
      var restBottom = m.bottom - rendered;

      var lift = 0;
      for (var i = 0; i < targets.length; i++) {
        var el = targets[i];
        if (!onScreen(el)) continue;
        var q = el.getBoundingClientRect();
        if (q.width < 8 || q.height < 8) continue;
        /* A 12px margin, so it clears rather than merely misses. */
        if (q.right > m.left - 12 && q.left < m.right + 12 &&
            q.bottom > restTop - 12 && q.top < restBottom + 12) {
          lift = Math.max(lift, Math.ceil(restBottom - q.top) + 14);
        }
      }
      /* Bounded, so a tall stack of buttons can never walk the mark up the
         screen and leave it stranded in the middle of the page. */
      if (lift > 220) lift = 220;

      /* And a deadband, so a couple of pixels of sub-pixel drift never
         restarts a 420ms spring. It moves when there is a reason to. */
      var want = lift ? -lift : 0;
      var raw = fan.style.getPropertyValue('--lmd-lift');
      var have = parseFloat(raw) || 0;
      if (raw !== '' && Math.abs(want - have) < 3) return;
      fan.style.setProperty('--lmd-lift', want + 'px');
    }

    /* The product page's buy bar slides up on a transition AFTER the scroll
       that called it - so the last scroll event measured a bar still below
       the screen, and when a thumb stopped right there the mark settled on
       Add to cart. One more reading when any transition carrying a target
       ends, and one trailing reading after the scrolling stops. */
    var trail = 0;
    this.onClear = function () {
      if (!ticking) { ticking = true; requestAnimationFrame(measure); }
      clearTimeout(trail);
      trail = setTimeout(function () {
        if (!ticking) { ticking = true; requestAnimationFrame(measure); }
      }, 560);
    };
    this.onClearEnd = function (e) {
      var t = e.target;
      if (t && t.nodeType === 1 && (t.matches(sel) || t.querySelector(sel))) self.onClear();
    };
    window.addEventListener('scroll', this.onClear, { passive: true });
    window.addEventListener('resize', this.onClear, { passive: true });
    window.addEventListener('load', this.onClear);
    document.addEventListener('transitionend', this.onClearEnd, true);
    this.clearTrail = function () { clearTimeout(trail); };

    /* Landing at the top of a product page fires no scroll event, so the first
       measurement is the only one that will ever happen there — and it happens
       before the images have decided how tall they are. Take it again as the
       page settles, and once more whenever the document changes height. */
    this.clearT = [setTimeout(this.onClear, 400), setTimeout(this.onClear, 1400)];
    if (typeof ResizeObserver === 'function') {
      this.clearRO = new ResizeObserver(this.onClear);
      this.clearRO.observe(document.documentElement);
    }

    measure();
  };

  /**
   * The stagger, indexed over the doors actually being shown.
   *
   * The reference staggers its menu by position in the rendered list. Door one
   * is removed whenever there is no piece and no room to talk about, so
   * counting in CSS with nth-child would leave a hole in the rhythm exactly
   * when the menu is shortest. So the order is written here instead:
   *
   *   --i  arrival order, first door first
   *   --o  departure order, last door first
   *
   * Two custom properties and no timers. The CSS multiplies them by the step
   * and the browser owns the scheduling, which means an interrupted close
   * reverses cleanly instead of fighting a queue of setTimeouts.
   */
  Desk.prototype.fanIndex = function () {
    var fan = this.fanEl;
    if (!fan) return;
    var all = fan.querySelectorAll('.lmd__fd');
    var shown = [];
    for (var i = 0; i < all.length; i++) {
      if (!all[i].hidden) shown.push(all[i]);
    }
    for (var j = 0; j < shown.length; j++) {
      /* Strings, not numbers - setProperty is specified to take a string and
         the coercion is only a browser kindness. */
      shown[j].style.setProperty('--i', String(j));
      shown[j].style.setProperty('--o', String(shown.length - 1 - j));
    }
  };

  Desk.prototype.fanShow = function () {
    if (this.fanOpen) return;
    this.fanOpen = true;
    /* Re-read on every open: a piece may have been set aside since last time. */
    this.fanContext();
    /* And re-index, because that read may have added or removed door one. */
    this.fanIndex();
    /* Let go of the lean before opening, or the doors fan out from a mark that
       is sitting a few pixels off its own centre. */
    if (this.fanToggleEl) { this.fanToggleEl.style.transform = ''; this.fanToggleEl.classList.remove('is-near'); }
    if (this.lmc) this.lmcReset();
    this.fanEl.classList.add('is-open');
    this.root.classList.add('is-fan-open');
    /* While the Desk is open, other floating things step aside (Lumi's
       launcher sits above everything and would float over the card). */
    document.documentElement.classList.add('lmd-on');
    this.fanToggleEl.setAttribute('aria-expanded', 'true');
    if (this.lmc) {
      var firstRow = this.lmc.querySelector('.lmc__door');
      if (firstRow) window.setTimeout(function () { try { firstRow.focus({ preventScroll: true }); } catch (e) {} }, 380);
    }
  };

  Desk.prototype.fanHide = function () {
    if (!this.fanOpen) return;
    this.fanOpen = false;
    this.fanEl.classList.remove('is-open');
    this.root.classList.remove('is-fan-open');
    if (!this.open) document.documentElement.classList.remove('lmd-on');
    this.fanToggleEl.setAttribute('aria-expanded', 'false');
    /* Measurement was frozen while it was open. Take it again on the way out,
       in case the page moved underneath. */
    if (this.onClear) this.onClear();
  };

  Desk.prototype.doorById = function (id) {
    if (!id) return null;
    for (var i = 0; i < this.doors.length; i++) {
      if (this.doors[i].dataset.lmdDoor === id) return this.doors[i];
    }
    return null;
  };

  /* One line of house knowledge, chosen per visit and then left alone. */
  Desk.prototype.pickKnowledge = function () {
    var el = this.root.querySelector('[data-lmd-know]');
    if (!el) return;
    var lines = (el.dataset.lines || '').split('\n')
      .map(function (l) { return l.trim(); })
      .filter(Boolean);
    if (!lines.length) return;
    el.textContent = lines[Math.floor(Math.random() * lines.length)];
  };

  /* On a return visit, say which door they came through last time. */
  Desk.prototype.markChosen = function () {
    if (!this.remember) return;
    var btn = this.doorById(read(KEY_DOOR));
    if (!btn) return;
    var li = btn.closest('.lmd__door');
    if (li) li.classList.add('is-chosen');
    if (this.rail) {
      var label = this.rail.querySelector('.lmd__rail-label');
      if (label && btn.dataset.label) label.textContent = btn.dataset.label;
      this.rail.setAttribute('title', 'Shift-click to choose a different door');
    }
  };


  /* ======================================================================
     THE DESK
     ----------------------------------------------------------------------
     Four doors in the customer's own words. One tap, and WhatsApp opens with
     a real first line already written - no questionnaire, no second screen.
     The conversation is where the particulars belong.

     Door 01 knows what is on screen and says it for them: the piece they are
     looking at, the cart they are standing in, the collection they are
     browsing. The first line reads as if the house already understood.

     THE ONE RULE still holds: every door calls sendText() inside its own
     click, so window.open is synchronous and no popup blocker can eat it.
     ====================================================================== */

  var KEY_SAID = 'lm-desk-said';
  var MAX_LINES = 20;         /* a wa.me URL is not infinite */

  Desk.prototype.lmcInit = function () {
    var self = this;
    var card = this.root.querySelector('[data-lmc]');
    if (!card) return;
    this.lmc = card;
    this.lmcCart = null;

    card.querySelectorAll('[data-lmc-door], [data-lmc-plain]').forEach(function (b) {
      b.addEventListener('click', function () {
        /* Synchronous, inside the click - the handover happens first. */
        self.sendText(self.lmcMessage(b), b.dataset.trade === '1');
        self.fanHide();
      });
    });

    var close = card.querySelector('[data-lmc-close]');
    if (close) close.addEventListener('click', function () {
      self.fanHide();
      if (self.fanToggleEl) self.fanToggleEl.focus();
    });

    /* The edges say there is more: a hairline under the head once the words
       have moved, and the last line softening while any are still below. */
    this.lmcStage = card.querySelector('[data-lmc-stage]');
    if (this.lmcStage) this.lmcStage.addEventListener('scroll', function () { self.lmcEdges(); }, { passive: true });

    /* The letterhead's clock (paper look): the time in Lahore, kept while open. */
    this.lmcClock = card.querySelector('[data-lmc-clock]');

    /* On the cart: the cart as drawn, kept current as it changes. */
    if (card.dataset.onCart === '1') {
      var tag = card.querySelector('[data-lmc-cart]');
      if (tag) { try { this.lmcCart = JSON.parse(tag.textContent || '[]'); } catch (e) { this.lmcCart = []; } }
      this.onCartChange = function () { self.lmcFetchCart(); };
      ['lh:cart:changed', 'cart:updated', 'cart:refresh', 'cart:change'].forEach(function (ev) {
        document.addEventListener(ev, self.onCartChange);
      });
    }
  };

  /* Read ahead of time, so the click that sends never waits on the network. */
  Desk.prototype.lmcFetchCart = function () {
    var self = this;
    if (!window.fetch) return;
    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (c) {
        var shop = self.root.dataset.shop || location.origin;
        self.lmcCart = (c.items || []).map(function (it) {
          return {
            t: it.product_title || it.title,
            q: it.quantity,
            p: 'Rs ' + Math.round((it.final_line_price || it.line_price || 0) / 100).toLocaleString('en-US'),
            u: shop + (it.url || ''),
            i: it.image || ''
          };
        });
        if (self.fanOpen) self.lmcContext();
      })
      .catch(function () {});
  };

  /* "Agra - Florin - 4.9 X 6.7 ft" -> "Agra Florin (4.9 × 6.7 ft)" */
  Desk.prototype.lmcSay = function (title) {
    var parts = String(title || '').split('-').map(function (p) { return p.trim(); }).filter(Boolean);
    if (!parts.length) return '';
    var size = '';
    if (parts.length > 1 && /x/i.test(parts[parts.length - 1])) {
      size = parts.pop().replace(/\s[xX]\s/g, ' × ');
    }
    var name = parts.join(' ').replace(/3ply/g, '3-ply').replace(/2ply/g, '2-ply');
    return size ? name + ' (' + size + ')' : name;
  };

  /*
   * What door 01 is about, in order: the piece on screen, the cart being
   * looked at, any pieces set aside, the collection being browsed. Nothing
   * on screen, and the door simply stands as written.
   */
  Desk.prototype.lmcAbout = function () {
    var c = this.lmc.dataset;
    if (c.pieceLabel) return { kind: 'piece' };
    if (c.onCart === '1' && this.lmcCart && this.lmcCart.length) return { kind: 'list', items: this.lmcCart };
    var aside = this.aside();
    if (aside.length) return { kind: 'list', items: aside.map(function (p) { return { t: p.title, p: p.price, u: p.url, q: 1, i: p.image || '' }; }) };
    if (c.room) return { kind: 'room' };
    return { kind: 'none' };
  };

  Desk.prototype.lmcContext = function () {
    var c = this.lmc, d = c.dataset;
    var box = c.querySelector('[data-lmc-ctx]');
    if (!box) return;
    var img = box.querySelector('[data-lmc-ctx-img]');
    var name = box.querySelector('[data-lmc-ctx-name]');
    var meta = box.querySelector('[data-lmc-ctx-meta]');
    var about = this.lmcAbout();
    var pic = '';

    if (about.kind === 'piece') {
      name.textContent = d.pieceName || d.pieceLabel;
      meta.textContent = d.pieceMeta || '';
      pic = d.pieceImg || '';
    } else if (about.kind === 'list') {
      var n = about.items.reduce(function (a, it) { return a + (it.q || 1); }, 0);
      name.textContent = about.items.length === 1 ? this.lmcSay(about.items[0].t) : n + ' pieces';
      meta.textContent = d.tagCart || '';
      pic = (about.items[0] && about.items[0].i) || '';
    } else if (about.kind === 'room') {
      name.textContent = d.room;
      meta.textContent = d.tagRoom || '';
      pic = d.roomImg || '';
    } else {
      box.hidden = true;
      return;
    }
    img.style.backgroundImage = pic ? 'url("' + pic.replace(/"/g, '%22') + '")' : '';
    img.hidden = !pic;
    box.hidden = false;
    this.lmcEdges();
  };

  /* The first line each door sends. Door 01 speaks about what is on screen. */
  Desk.prototype.lmcMessage = function (btn) {
    if (btn.dataset.lmcDoor !== '1') return btn.dataset.msg || 'Hi Loomish —';
    var d = this.lmc.dataset;
    var about = this.lmcAbout();
    var self = this;

    if (about.kind === 'piece') {
      var one = (d.m1Piece || '').replace(/\[piece\]/g, d.pieceLabel);
      return d.pieceLink ? one + '\n' + d.pieceLink : one;
    }
    if (about.kind === 'list') {
      var lines = [d.m1Cart || '', ''];
      about.items.slice(0, MAX_LINES).forEach(function (it, i) {
        var bits = [self.lmcSay(it.t)];
        if (it.q > 1) bits.push('× ' + it.q);
        if (it.p) bits.push(it.p);
        lines.push(('0' + (i + 1)).slice(-2) + ' · ' + bits.join(' · '));
        if (it.u) lines.push(it.u);
      });
      if (about.items.length > MAX_LINES) lines.push('… and ' + (about.items.length - MAX_LINES) + ' more.');
      return lines.join('\n');
    }
    if (about.kind === 'room') return (d.m1Room || '').replace(/\[collection\]/g, d.room);
    return d.m1 || btn.dataset.msg || 'Hi Loomish —';
  };

  /*
   * THE FIRST WORD. Three dots, as if somebody at the desk is writing, and
   * then the line arrives. Once a visit, and never in the way: the doors are
   * there and tappable from the first frame. Every later open just says it.
   */
  Desk.prototype.lmcIntro = function () {
    var c = this.lmc;
    if (!c) return;
    window.clearTimeout(this.lmcIntroT);
    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var said = false;
    try { said = sessionStorage.getItem(KEY_SAID) === '1'; } catch (e) {}
    if (reduce || said) { c.classList.add('is-said'); return; }
    c.classList.remove('is-said');
    try { sessionStorage.setItem(KEY_SAID, '1'); } catch (e) {}
    this.lmcIntroT = window.setTimeout(function () { c.classList.add('is-said'); }, 820);
  };

  Desk.prototype.lmcEdges = function () {
    var s = this.lmcStage;
    if (!s) return;
    this.lmc.classList.toggle('is-scrolled', s.scrollTop > 2);
    this.lmc.classList.toggle('has-more', s.scrollTop + s.clientHeight < s.scrollHeight - 4);
  };

  /* Dressed for the current page every time it opens. */
  Desk.prototype.lmcReset = function () {
    this.lmcContext();
    this.lmcIntro();
    if (this.lmcStage) this.lmcStage.scrollTop = 0;
    this.lmcEdges();
    this.lmcTick();
  };

  /* Lahore, now - and again every twenty seconds while the card is open. */
  Desk.prototype.lmcTick = function () {
    var el = this.lmcClock;
    if (!el) return;
    var self = this;
    var t = '';
    try {
      t = new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'Asia/Karachi' }).format(new Date());
    } catch (e) {}
    el.textContent = (this.lmc.dataset.clock || '').replace('[time]', t).replace(/\s+/g, ' ').trim();
    window.clearTimeout(this.lmcClockT);
    this.lmcClockT = window.setTimeout(function () { if (self.fanOpen) self.lmcTick(); }, 20000);
  };

  /* ------------------------------------------------------------ the panel */

  Desk.prototype.show = function () {
    if (this.open) return;
    this.open = true;
    this.lastFocus = document.activeElement;

    if (this.scrim) this.scrim.hidden = false;
    if (this.panel) this.panel.hidden = false;
    /* A frame between "in the DOM" and "in its open state", or the transition
       has nothing to transition from. */
    var self = this;
    requestAnimationFrame(function () {
      requestAnimationFrame(function () { self.root.classList.add('is-open'); });
    });
    document.documentElement.classList.add('lmd-on');

    if (this.rail) this.rail.setAttribute('aria-expanded', 'true');

    var first = this.panel && this.panel.querySelector('[data-lmd-door]');
    if (first) setTimeout(function () { first.focus(); }, 60);
  };

  Desk.prototype.hide = function () {
    if (!this.open) return;
    this.open = false;
    this.root.classList.remove('is-open');
    if (!this.fanOpen) document.documentElement.classList.remove('lmd-on');
    if (this.rail) this.rail.setAttribute('aria-expanded', 'false');

    var self = this;
    /* Hidden only once it has finished leaving, so the exit is visible */
    window.setTimeout(function () {
      if (self.open) return;
      if (self.scrim) self.scrim.hidden = true;
      if (self.panel) self.panel.hidden = true;
    }, 480);

    if (this.lastFocus && this.lastFocus.focus) this.lastFocus.focus();
  };

  /* Focus stays in the panel while the panel is the page. */
  Desk.prototype.trap = function (e) {
    if (!this.panel) return;
    var f = this.panel.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
    if (!f.length) return;
    var first = f[0], last = f[f.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  };

  /* ---------------------------------------------------------- the message */

  /*
   * What the visitor was looking at travels with them. A shortlist beats a
   * single piece, because a shortlist is the conversation.
   */
  Desk.prototype.context = function () {
    var d = this.root.dataset;

    var aside = this.aside();
    if (aside.length) {
      var lines = ['', 'Set aside — ' + aside.length + (aside.length === 1 ? ' piece.' : ' pieces.')];
      var total = 0, priced = true;
      aside.slice(0, MAX_ASIDE).forEach(function (p, i) {
        var n = ('0' + (i + 1)).slice(-2);
        var bits = [p.title];
        if (p.price) bits.push(p.price); else priced = false;
        lines.push(n + ' · ' + bits.join(' · '));
        if (p.url) lines.push('     ' + p.url);
        var num = p.price ? parseFloat(String(p.price).replace(/[^0-9.]/g, '')) : NaN;
        if (isNaN(num)) priced = false; else total += num;
      });
      if (aside.length > MAX_ASIDE) lines.push('… and ' + (aside.length - MAX_ASIDE) + ' more.');
      else if (priced && total > 0) lines.push('', 'As listed: Rs ' + total.toLocaleString('en-US'));
      return lines.join('\n');
    }

    if (d.piece) {
      var one = ['', 'Looking at: ' + d.piece];
      if (d.piecePrice) one[1] += ' · ' + d.piecePrice;
      if (d.pieceUrl) one.push(d.pieceUrl);
      return one.join('\n');
    }

    if (d.room) return '\nBrowsing: ' + d.room;
    return '';
  };

  Desk.prototype.aside = function () {
    var raw = read(KEY_ASIDE);
    if (!raw) return [];
    try {
      var a = JSON.parse(raw);
      return Array.isArray(a) ? a : [];
    } catch (e) { return []; }
  };

  Desk.prototype.compose = function (btn) {
    var out = [btn.dataset.prefill || 'Hello Loomish.'];

    /* The stub: three lines the sender can complete, so the first reply can be
       numbers rather than questions. Trade doors only — retail stays a
       conversation, and a form is the fastest way to end one. */
    if (btn.dataset.stub === '1') {
      var fields = (btn.dataset.stubFields || '').split(',')
        .map(function (f) { return f.trim(); })
        .filter(Boolean);
      if (fields.length) {
        out.push('');
        fields.forEach(function (f) { out.push(f + ': '); });
      }
      if (btn.dataset.stubNote) out.push('', btn.dataset.stubNote);
    }

    var ctx = this.context();
    if (ctx) out.push(ctx);

    return out.join('\n');
  };

  /* ---------------------------------------------------------- the handover */

  /*
   * WhatsApp in a new tab, synchronously, inside the click.
   *
   * NOT with the 'noopener' window feature. By the spec, window.open then
   * returns null even when the tab has opened - and the fallback below read
   * that null as "blocked" and sent THIS tab to WhatsApp too. Every door left
   * the shop behind: the visitor came back from WhatsApp to a dead WhatsApp
   * page instead of the piece they were looking at (measured 2 Oct). The
   * opener is cut by hand instead, which is all 'noopener' was for.
   */
  function openOut(url) {
    /* INSIDE AN APP (2026-10-25). Instagram's and Facebook's own browsers
       have no tabs: what a new window becomes is up to the app, and an app
       can take the call and show nothing - the tap goes in and no
       conversation opens. The road every one of them does take is the one
       their own bio links take: the page goes to wa.me, and the phone hands
       it to WhatsApp. (html.lm-app: theme.liquid, INSIDE AN APP.) */
    if (document.documentElement.classList.contains('lm-app')) { window.location.href = url; return false; }
    var win = null;
    try { win = window.open(url, '_blank'); } catch (e) {}
    if (win) { try { win.opener = null; } catch (e) {} return true; }
    window.location.href = url;   /* genuinely blocked: go there instead */
    return false;
  }

  Desk.prototype.send = function (btn, direct) {
    var number = btn.dataset.trade === '1' ? this.waTrade : this.wa;
    var text = this.compose(btn);
    var url = 'https://wa.me/' + number + '?text=' + encodeURIComponent(text);

    /* FIRST. Synchronously, inside the click. Everything else is decoration. */
    openOut(url);

    if (this.remember && btn.dataset.lmdDoor !== 'plain') store(KEY_DOOR, btn.dataset.lmdDoor);

    if (!direct) this.hide();
    this.farewell();
  };

  Desk.prototype.sendText = function (text, trade) {
    var number = trade ? this.waTrade : this.wa;
    var url = 'https://wa.me/' + number + '?text=' + encodeURIComponent(text);
    /* FIRST. Synchronously, inside the click. */
    openOut(url);
    this.farewell();
  };

  Desk.prototype.farewell = function () {
    var el = this.leaving;
    if (!el || !el.textContent.trim()) return;
    el.hidden = false;
    var self = this;
    requestAnimationFrame(function () { el.classList.add('is-on'); });
    window.clearTimeout(this.byeT);
    this.byeT = window.setTimeout(function () {
      el.classList.remove('is-on');
      window.setTimeout(function () { el.hidden = true; }, 400);
    }, 2400);
  };

  Desk.prototype.destroy = function () {
    document.removeEventListener('keydown', this.onKey);
    window.clearTimeout(this.byeT);
    window.clearTimeout(this.lmcIntroT);
    window.clearTimeout(this.lmcClockT);
    if (this.onCartChange) {
      var cc = this.onCartChange;
      ['lh:cart:changed', 'cart:updated', 'cart:refresh', 'cart:change'].forEach(function (ev) {
        document.removeEventListener(ev, cc);
      });
    }
    this.root.classList.remove('is-open');

    /* The fan puts two listeners on document and one element outside itself.
       A Theme Editor session reloads this section repeatedly, so leaving them
       behind would stack a new pair on every save. */
    if (this.onFanKey) document.removeEventListener('keydown', this.onFanKey);
    if (this.onFanOut) document.removeEventListener('pointerdown', this.onFanOut);
    if (this.onLean) window.removeEventListener('pointermove', this.onLean);
    if (this.onClear) {
      window.removeEventListener('scroll', this.onClear);
      window.removeEventListener('resize', this.onClear);
      window.removeEventListener('load', this.onClear);
    }
    if (this.onClearEnd) document.removeEventListener('transitionend', this.onClearEnd, true);
    if (this.clearTrail) this.clearTrail();
    if (this.clearT) { this.clearT.forEach(window.clearTimeout); }
    if (this.clearRO) { this.clearRO.disconnect(); }
    if (this.veil && this.veil.parentNode) this.veil.parentNode.removeChild(this.veil);
    this.root.classList.remove('is-fan-open');
    document.documentElement.classList.remove('lmd-on');
  };

  /* ------------------------------------------------------------------ boot */

  function sweep(scope) {
    for (var i = live.length - 1; i >= 0; i--) {
      var t = live[i];
      var gone = !document.body.contains(t.root);
      var inScope = scope && scope.contains && scope.contains(t.root);
      if (!gone && !inScope) continue;
      try { t.destroy(); } catch (e) {}
      t.root.__lmd = null;
      t.root.removeAttribute('data-mounted');
      live.splice(i, 1);
    }
  }

  function boot() {
    var nodes = document.querySelectorAll('[data-lmd]:not([data-mounted])');
    for (var i = 0; i < nodes.length; i++) {
      nodes[i].dataset.mounted = '1';
      try { new Desk(nodes[i]); }
      catch (e) { if (window.console) console.error('[loomish-desk]', e); }
    }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  /* ------------------------------------------- doors elsewhere (2026-10-14)

     ONE SENDER. Any link on the site that asks the house about something in
     particular - a collection still in the making, the right length of
     runner, a family held as single pieces - carries its message in
     data-lmd-ask and a working wa.me address in its href (written by
     snippets/loomish-wa.liquid). At the click the Desk sends it instead:
     its own number, the new tab opened synchronously inside the click, the
     farewell. So every door of the house leaves the same way, and a link no
     script ever reached still opens the right conversation by itself.

     Bound once, for the whole document, so no section needs a handler of its
     own and nothing can send twice. A modifier-click is left to the browser. */
  document.addEventListener('click', function (e) {
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    var door = e.target.closest && e.target.closest('[data-lmd-ask]');
    if (!door) return;
    var text = door.getAttribute('data-lmd-ask');
    var desk = null;
    for (var i = 0; i < live.length; i++) {
      if (document.body.contains(live[i].root)) { desk = live[i]; break; }
    }
    if (!desk || !text) return;
    e.preventDefault();
    desk.sendText(text, door.getAttribute('data-trade') === '1');
  });

  document.addEventListener('shopify:section:load', function (e) { sweep(e.target); boot(); });
  document.addEventListener('shopify:section:unload', function (e) { sweep(e.target); });
})();
